'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { AUTH_CONFIG } from '@eliadmin/core/src/constants/auth-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LiaEnvelope } from 'react-icons/lia';
import { BsKey } from 'react-icons/bs';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';

export const signInSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
};

export function SignInForm({ onSuccess, initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col items-center justify-center gap-6', 'ea-sign-in-form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-3',
            'ea-sign-in-form__header',
          )}
        >
          {inDialog ? (
            <h4 className="text-center text-2xl font-medium tracking-tight sm:text-3xl">
              {t('text190')}
            </h4>
          ) : (
            <h1 className="text-center text-2xl font-medium tracking-tight sm:text-3xl">
              {t('text190')}
            </h1>
          )}
          <p className="text-sm sm:text-base">
            {t('text2292')}&nbsp;
            {inDialog ? (
              <DialogClose>
                <a href={ROUTES.REGISTER() ?? '#'} className="font-semibold lowercase text-primary">
                  {t('text2019')}.
                </a>
              </DialogClose>
            ) : (
              <Link
                href={ROUTES.REGISTER() ?? '#'}
                className="font-semibold lowercase text-primary"
              >
                {t('text2019')}.
              </Link>
            )}
          </p>
        </div>

        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-4',
            'ea-sign-in-form__content',
          )}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem required className={cn('w-full', 'ea-sign-in-form__email-field')}>
                <FormControl className={cn('w-full', 'ea-sign-in-form__email-input')}>
                  <Input
                    {...field}
                    placeholder={t('text35')}
                    type="email"
                    before={<LiaEnvelope size={24} className="text-[#636363]" />}
                  />
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__email-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className={cn('w-full', 'ea-sign-in-form__password-field')}>
                <FormControl className={cn('w-full', 'ea-sign-in-form__password-input')}>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      type={showPassword ? 'text' : 'password'}
                      before={<BsKey size={24} className="text-[#636363]" />}
                      after={
                        <button
                          type="button"
                          onMouseDown={handlePasswordMouseDown}
                          onMouseUp={handlePasswordMouseUp}
                          onMouseLeave={handlePasswordMouseUp}
                          onTouchStart={handlePasswordMouseDown}
                          onTouchEnd={handlePasswordMouseUp}
                        >
                          {showPassword ? (
                            <FaRegEyeSlash size={20} className="text-primary" />
                          ) : (
                            <FaRegEye size={20} className="text-primary" />
                          )}
                        </button>
                      }
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center gap-6',
            'ea-sign-in-form__footer',
          )}
        >
          <Button
            type="submit"
            className={cn('w-full sm:w-fit', 'ea-sign-in-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text189')}
          </Button>
          {AUTH_CONFIG.signIn.passwordResetPosition === 'inside' && (
            <div className={cn('mx-auto flex justify-center', 'ea_sign-in-form__password-reset')}>
              <Link
                className={cn(
                  'w-fit text-center text-sm font-semibold tracking-tight text-primary sm:text-base',
                  'ea_sign-in-form__password-reset-link ',
                )}
                href={ROUTES.PASSWORD_RESET() ?? '#'}
                prefetch={false}
              >
                {t('text2345')}
              </Link>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
